import { Dispatch, SetStateAction, useCallback } from 'react';
import cn from 'classnames';
import { MdArrowDownward, MdArrowUpward } from 'react-icons/md';
import { useDrag, useDrop } from 'react-dnd';
import { SortInfo } from '@blisspointmedia/bpm-types/dist/Performance';
import { InteractiveDiv } from '@Components/InteractiveEl';

const SORT_ITEM_DND_TYPE = 'SORT_ITEM_DND_TYPE' as const;

interface SortItemProps {
  item: SortInfo;
  colorMap: Record<string, string | undefined>;
  setHoverItem: Dispatch<SetStateAction<string | undefined>>;
  setNewSortInfo: (func: (arr: SortInfo[]) => SortInfo[]) => void;
  onClick: () => void;
}

export const SortItem = ({
  item,
  colorMap,
  setHoverItem,
  setNewSortInfo,
  onClick
}: SortItemProps) => {
  const { id, asc } = item;

  const [, drag, preview] = useDrag<SortInfo & { type: typeof SORT_ITEM_DND_TYPE }>({
    type: SORT_ITEM_DND_TYPE,
    item: { type: SORT_ITEM_DND_TYPE, id, asc }
  });

  const [{ isOver }, drop] = useDrop<
    SortInfo & { type: typeof SORT_ITEM_DND_TYPE },
    void,
    { isOver: boolean }
  >({
    accept: SORT_ITEM_DND_TYPE,
    canDrop: dragged => dragged.id !== id,
    collect: monitor => ({
      isOver: monitor.isOver() && monitor.canDrop()
    }),
    drop: draggedItem => {
      setNewSortInfo(existing =>
        existing.reduce<SortInfo[]>((agg, elem) => {
          // If it's us, then drop in front of us
          if (elem.id === id) {
            const { type: _type, ...draggedSortInfo } = draggedItem;
            return [...agg, draggedSortInfo, elem];
          }
          // If it's the item we're dragging, don't include it in our new list
          if (elem.id === draggedItem.id) {
            return agg;
          }
          return [...agg, elem];
        }, [])
      );
    }
  });

  const dragDropRef = useCallback(
    (node: HTMLDivElement | null) => {
      preview(node);
      drag(node);
      drop(node);
    },
    [preview, drag, drop]
  );

  return (
    <InteractiveDiv
      ref={dragDropRef}
      style={{ borderColor: colorMap[id] || 'black' }}
      className={cn('sort-item', { 'is-over': isOver })}
      onMouseOver={() => setHoverItem(id)}
      onFocus={() => setHoverItem(id)}
      onMouseLeave={() => setHoverItem(current => (current === id ? undefined : current))}
      onClick={() => {
        if (!asc) {
          setHoverItem(undefined);
        }
        onClick();
      }}
    >
      {asc ? <MdArrowUpward /> : <MdArrowDownward />}
    </InteractiveDiv>
  );
};
